/**
 * Profile frames, drawn rather than shipped.
 *
 * The old frames were PNGs - well over a megabyte each, fixed resolution, and
 * they had to overhang the picture by two thirds to look right, which is what
 * made them so big on screen. These are CSS: a few hundred bytes, sharp at any
 * size, and able to move.
 *
 * A frame is a ring drawn just outside the picture. Nothing overhangs, so the
 * avatar stays the size it says it is and rows do not reflow when somebody
 * equips one.
 */

import { cn } from '@/lib/cn';

export type FrameDef = {
  id: string;
  name: string;
  desc: string;
  /** Frames that move. Held still under Reduce motion. */
  animated?: boolean;
  /** The colour it lends to whatever it sits on. */
  glow?: string;
};

export const FRAMES: FrameDef[] = [
  { id: 'none',    name: 'None',    desc: 'Just your picture.' },
  { id: 'ring',    name: 'Ring',    desc: 'A clean line in your accent colour.', glow: 'var(--accent)' },
  { id: 'gold',    name: 'Gold',    desc: 'Warm metal, double banded.', glow: '#e8b64c' },
  { id: 'frost',   name: 'Frost',   desc: 'Cold blue, with an inner halo.', glow: '#6fd8ff' },
  { id: 'ember',   name: 'Ember',   desc: 'Banked coals. Breathes slowly.', animated: true, glow: '#ff7a3d' },
  { id: 'neon',    name: 'Neon',    desc: 'Violet to ember, turning.', animated: true, glow: '#7b5cff' },
  { id: 'aurora',  name: 'Aurora',  desc: 'Four colours, drifting.', animated: true, glow: '#43e0b0' },
  { id: 'pulse',   name: 'Pulse',   desc: 'A ring that keeps time.', animated: true, glow: '#ff5f8f' },
  { id: 'circuit', name: 'Circuit', desc: 'Dashed and rotating.', animated: true, glow: '#8ee06a' },
  { id: 'royal',   name: 'Royal',   desc: 'Gold and sapphire, crowned.', glow: '#ffd76a' },
  { id: 'forge',   name: 'Forge',   desc: 'Blacked-out plating with gold trim.', glow: '#f5b301' },
  { id: 'prism',   name: 'Prism',   desc: 'The full spectrum, turning fast.', animated: true, glow: '#47e5bc' },
  { id: 'comet',   name: 'Comet',   desc: 'One bright head chasing its tail.', animated: true, glow: '#6fd8ff' },
  { id: 'tide',    name: 'Tide',    desc: 'Deep water, rising and falling.', animated: true, glow: '#22d3ee' },
  { id: 'magma',   name: 'Magma',   desc: 'Cooling crust with fire underneath.', animated: true, glow: '#f97316' },
  { id: 'void',    name: 'Void',    desc: 'Violet, lit from somewhere behind.', animated: true, glow: '#7c3aed' },
  { id: 'sakura',  name: 'Sakura',  desc: 'Soft pink, drifting.', animated: true, glow: '#f9a8d4' },
  { id: 'static',  name: 'Static',  desc: 'Broken signal, flickering.', animated: true, glow: '#e5e7eb' },
  { id: 'halo',    name: 'Halo',    desc: 'A thin gold ring that breathes.', animated: true, glow: '#f5dd8f' }
];

export function frameDef(id: string | undefined): FrameDef | undefined {
  return FRAMES.find((f) => f.id === id);
}

/** The colour a frame lends, or null when there is no frame. */
export function frameGlow(frame: string | undefined): string | null {
  if (!frame || frame === 'none') return null;
  return frameDef(frame)?.glow ?? null;
}

/**
 * The ring itself, sized to whatever it wraps.
 *
 * Absolutely positioned and inset by a negative amount, so it hugs the picture
 * from outside without taking part in layout.
 */
export function FrameRing({ frame, size, radius, className }: {
  frame: string;
  size: number;
  /** Match a square picture instead of the default squircle. */
  radius?: string;
  className?: string;
}) {
  if (!frame || frame === 'none') return null;

  // The ring scales with the avatar so a 22px row mark and a 96px profile
  // picture wear the same frame at the same visual weight.
  const band = Math.max(2, Math.round(size * 0.055));
  const gap = Math.max(1, Math.round(size * 0.03));
  const out = band + gap;

  return (
    <span
      aria-hidden
      data-frame={frame}
      className={cn('pointer-events-none absolute', !radius && 'rounded-[var(--avatar-radius,50%)]', 'frame-' + frame, className)}
      style={{
        inset: -out,
        // A square picture wants a square frame around it, sized so the band
        // sits parallel to the edge rather than cutting across the corner.
        ...(radius ? { borderRadius: 'calc(' + radius + ' + ' + out + 'px)' } : null),
        ['--band' as string]: band + 'px'
      }}
    />
  );
}

/* ------------------------------------------------- the profile effect */

/**
 * The frame, applied to a whole profile rather than a picture.
 *
 * Discord calls these profile effects. Same idea: the frame you equip also
 * decorates the card or hero behind you - a tinted edge, a glow along the top,
 * and for the moving ones a slow sweep across the banner.
 *
 * Sits behind the content and never takes pointer events, so it decorates
 * without getting in the way of anything.
 */
export function FrameEffect({ frame, className }: { frame: string | undefined; className?: string }) {
  if (!frame || frame === 'none') return null;
  const glow = frameGlow(frame);
  if (!glow) return null;

  const moving = frameDef(frame)?.animated;

  return (
    <span
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]', className)}
      style={{ ['--fx' as string]: glow }}
    >
      {/* a wash from the top edge, in the frame's colour */}
      <span
        className="absolute inset-x-0 top-0 h-[62%]"
        style={{ background: 'linear-gradient(180deg, color-mix(in srgb, var(--fx) 26%, transparent), transparent)' }}
      />
      {/* the edge itself */}
      <span
        className="absolute inset-0 rounded-[inherit]"
        style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--fx) 45%, transparent)' }}
      />
      {/* and a slow sweep, only for the frames that move */}
      {moving ? (
        <span
          className="fx-sweep absolute inset-y-0 w-[42%]"
          style={{
            background: 'linear-gradient(100deg, transparent, color-mix(in srgb, var(--fx) 30%, transparent), transparent)'
          }}
        />
      ) : null}
    </span>
  );
}
